/**
 * BroadcastInfoPanel — what a viewer at home needs beside the video.
 *
 * Someone in the building can read the notice sheet, see the slide on the wall
 * and drop something in the offering as it passes. Someone watching on a phone
 * two hundred miles away could do none of those things: the reading went by
 * unheard, the slide was off camera, and giving meant leaving the stream to
 * hunt for a page they had never seen. This strip puts all three beside the
 * player.
 *
 * The giving card shows a QR code because a phone cannot scan its own screen —
 * it is for the person watching on a laptop or a television, while the numbers
 * beneath it, each copyable in one tap, are for the person watching on the
 * phone itself. Give Now opens the church's existing giving page in a panel so
 * the sermon keeps playing behind it.
 *
 * Nothing is invented for display: where a broadcast has said nothing, this
 * renders nothing rather than an empty heading.
 */
import React, { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { QRCodeSVG } from 'qrcode.react';
import {
  HandHeart, BookOpen, Mic2, ListOrdered, Copy, Check, ExternalLink,
  Presentation, Landmark, Megaphone,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import api from '@/lib/api';

const SKIN = {
  dark: {
    card: 'bg-slate-900 border-slate-800',
    heading: 'text-slate-100',
    body: 'text-slate-300',
    muted: 'text-slate-500',
    accent: 'text-emerald-400',
    chip: 'bg-slate-800 border-slate-700 text-slate-200',
  },
  light: {
    card: 'bg-white border-slate-200',
    heading: 'text-slate-900',
    body: 'text-slate-700',
    muted: 'text-slate-500',
    accent: 'text-emerald-700',
    chip: 'bg-slate-50 border-slate-200 text-slate-700',
  },
};

const CopyLine = ({ label, value, skin }) => {
  const [done, setDone] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setDone(true);
      setTimeout(() => setDone(false), 1800);
    } catch { /* a browser that will not copy still shows the number */ }
  };
  return (
    <button
      type="button"
      onClick={copy}
      className={`flex w-full items-center justify-between gap-3 rounded-lg border px-3 py-2 text-left transition-colors hover:border-emerald-500/60 ${skin.chip}`}
      data-testid={`giving-copy-${(label || value).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
    >
      <span className="min-w-0">
        {label ? <span className="block text-[11px] uppercase tracking-wide opacity-60">{label}</span> : null}
        <span className="block truncate font-mono text-sm font-semibold">{value}</span>
      </span>
      {done
        ? <Check className="h-4 w-4 shrink-0 text-emerald-500" />
        : <Copy className="h-4 w-4 shrink-0 opacity-50" />}
    </button>
  );
};

const absolute = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const BroadcastInfoPanel = ({ scope, ident, tone = 'dark', live = true }) => {
  const skin = SKIN[tone] || SKIN.dark;
  const [giveOpen, setGiveOpen] = useState(false);
  const [brokenSlide, setBrokenSlide] = useState('');
  const closeRef = useRef(null);

  const { data } = useQuery({
    queryKey: ['broadcast-info', scope, ident],
    queryFn: () => api.get(`/live/${scope}/${ident}/info`).then((r) => r.data),
    enabled: !!scope && !!ident,
    // Slides move while a presenter is teaching, so this looks often enough to
    // keep up and stops entirely once the broadcast is over.
    refetchInterval: live ? 6000 : false,
    retry: false,
  });

  if (!data?.has_content) return null;

  const { giving = {}, slides = {}, order = [] } = data;
  const deck = slides.enabled ? (slides.images || []) : [];
  const chosen = deck[Math.min(slides.current || 0, Math.max(deck.length - 1, 0))];
  // A slide whose image will not load is left out rather than shown as a
  // broken frame in the middle of a service.
  const slide = chosen && chosen.url !== brokenSlide ? chosen : null;
  const qrTarget = giving.qr_mode === 'giving_page' ? absolute(giving.give_url) : '';
  const details = [data.speaker, data.series, data.scripture].filter(Boolean);

  return (
    <div className="mt-6 space-y-4" data-testid={`broadcast-info-${scope}`}>
      {slide ? (
        <Card className={skin.card} data-testid="broadcast-slide">
          <CardContent className="p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <span className={`flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide ${skin.accent}`}>
                <Presentation className="h-3.5 w-3.5" />On screen
              </span>
              <span className={`text-xs ${skin.muted}`} data-testid="broadcast-slide-position">
                Slide {(slides.current || 0) + 1} of {deck.length}
              </span>
            </div>
            <img
              src={slide.url}
              alt={slide.caption || `Slide ${(slides.current || 0) + 1}`}
              onError={() => setBrokenSlide(slide.url)}
              className="w-full rounded-lg border border-black/20 bg-black object-contain"
            />
            {slide.caption ? (
              <p className={`mt-2 text-sm ${skin.body}`}>{slide.caption}</p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {(data.title || details.length || data.notices || order.length) ? (
        <Card className={skin.card} data-testid="broadcast-about">
          <CardContent className="space-y-4 p-4">
            {data.title ? (
              <div>
                <p className={`flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] ${skin.accent}`}>
                  <Mic2 className="h-3 w-3" />Today
                </p>
                <h2 className={`mt-1 text-base font-bold md:text-lg ${skin.heading}`} data-testid="broadcast-about-title">
                  {data.title}
                </h2>
              </div>
            ) : null}

            {details.length ? (
              <div className="flex flex-wrap gap-2">
                {data.speaker ? (
                  <Badge variant="outline" className={skin.chip} data-testid="broadcast-speaker">
                    {data.speaker}
                  </Badge>
                ) : null}
                {data.series ? (
                  <Badge variant="outline" className={skin.chip} data-testid="broadcast-series">
                    {data.series}
                  </Badge>
                ) : null}
                {data.scripture ? (
                  <Badge variant="outline" className={skin.chip} data-testid="broadcast-scripture">
                    <BookOpen className="mr-1 h-3 w-3" />{data.scripture}
                  </Badge>
                ) : null}
              </div>
            ) : null}

            {order.length ? (
              <div>
                <p className={`flex items-center gap-1.5 text-xs font-semibold ${skin.heading}`}>
                  <ListOrdered className="h-3.5 w-3.5" />Order of service
                </p>
                <ol className="mt-2 space-y-1" data-testid="broadcast-order">
                  {order.map((o, i) => (
                    <li key={i} className={`flex gap-3 text-sm ${skin.body}`}>
                      <span className={`w-14 shrink-0 font-mono text-xs ${skin.muted}`}>{o.time}</span>
                      <span>{o.item}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            {data.notices ? (
              <div>
                <p className={`flex items-center gap-1.5 text-xs font-semibold ${skin.heading}`}>
                  <Megaphone className="h-3.5 w-3.5" />Notices
                </p>
                <p className={`mt-1 whitespace-pre-wrap text-sm ${skin.body}`} data-testid="broadcast-notices">
                  {data.notices}
                </p>
              </div>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {giving.enabled ? (
        <Card className={`${skin.card} border-emerald-500/30`} data-testid="broadcast-giving">
          <CardContent className="p-4">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              {(qrTarget || giving.qr_image_url) ? (
                <div className="shrink-0 self-center rounded-xl bg-white p-2.5" data-testid="broadcast-giving-qr">
                  {qrTarget ? (
                    <QRCodeSVG value={qrTarget} size={116} level="M" />
                  ) : (
                    <img src={giving.qr_image_url} alt="Giving QR code"
                         className="h-[116px] w-[116px] object-contain" />
                  )}
                </div>
              ) : null}

              <div className="min-w-0 flex-1 space-y-3">
                <div>
                  <h3 className={`flex items-center gap-2 text-base font-bold ${skin.heading}`}>
                    <HandHeart className={`h-4 w-4 ${skin.accent}`} />
                    {giving.heading}
                  </h3>
                  {giving.message ? (
                    <p className={`mt-1 whitespace-pre-wrap text-sm ${skin.body}`} data-testid="broadcast-giving-message">
                      {giving.message}
                    </p>
                  ) : null}
                  {qrTarget ? (
                    <p className={`mt-1 text-xs ${skin.muted}`}>
                      Scan the code, or tap Give Now on this device.
                    </p>
                  ) : null}
                </div>

                {giving.methods?.length ? (
                  <div className="grid gap-2 sm:grid-cols-2">
                    {giving.methods.map((m, i) => (
                      <div key={i}>
                        <CopyLine label={m.label} value={m.number || m.label} skin={skin} />
                        {m.instructions ? (
                          <p className={`mt-1 text-[11px] ${skin.muted}`}>{m.instructions}</p>
                        ) : null}
                      </div>
                    ))}
                  </div>
                ) : null}

                {Object.keys(giving.bank || {}).length ? (
                  <div className={`rounded-lg border p-3 ${skin.chip}`} data-testid="broadcast-giving-bank">
                    <p className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide opacity-70">
                      <Landmark className="h-3 w-3" />Bank transfer
                    </p>
                    <dl className="mt-1.5 space-y-0.5 text-sm">
                      {[['Bank', giving.bank.bank_name], ['Account name', giving.bank.account_name],
                        ['Account number', giving.bank.account_number], ['Branch', giving.bank.branch],
                        ['SWIFT', giving.bank.swift]].filter(([, v]) => v).map(([k, v]) => (
                        <div key={k} className="flex gap-2">
                          <dt className="w-32 shrink-0 text-xs opacity-60">{k}</dt>
                          <dd className="font-mono text-xs">{v}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                ) : null}

                {giving.give_url ? (
                  <Button
                    onClick={() => setGiveOpen(true)}
                    className="bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid="broadcast-give-now"
                  >
                    <HandHeart className="mr-1.5 h-4 w-4" />Give Now
                  </Button>
                ) : null}
              </div>
            </div>
          </CardContent>
        </Card>
      ) : null}

      <Dialog open={giveOpen} onOpenChange={setGiveOpen}>
        <DialogContent
          className="w-[calc(100vw-2rem)] max-w-3xl p-0 sm:w-full"
          data-testid="broadcast-give-dialog"
          // Without this the iframe takes focus, and Escape then belongs to the
          // giving page rather than the dialog — a keyboard user is trapped.
          onOpenAutoFocus={(e) => { e.preventDefault(); closeRef.current?.focus(); }}
        >
          <DialogHeader className="border-b px-4 py-3 pr-12">
            <DialogTitle className="flex items-center gap-2 text-base">
              <HandHeart className="h-4 w-4 text-emerald-600" />Give
              <a ref={closeRef} href={absolute(giving.give_url)} target="_blank"
                 rel="noopener noreferrer"
                 className="ml-auto inline-flex items-center gap-1 text-xs font-normal text-emerald-700 hover:underline"
                 data-testid="broadcast-give-open-tab">
                Open in a new tab <ExternalLink className="h-3 w-3" />
              </a>
            </DialogTitle>
          </DialogHeader>
          {giveOpen ? (
            <iframe
              src={absolute(giving.give_url)}
              title="Give"
              className="h-[70vh] w-full border-0"
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BroadcastInfoPanel;
